import { css } from "@emotion/react";
import { defaultKeymap } from "@codemirror/commands";
import { StreamLanguage } from "@codemirror/language";
import { yaml } from "@codemirror/legacy-modes/mode/yaml";
import { linter, type Diagnostic } from "@codemirror/lint";
import { githubDark, githubLight } from "@uiw/codemirror-theme-github";
import CodeMirror, {
  EditorView,
  keymap,
  type ReactCodeMirrorRef,
} from "@uiw/react-codemirror";
import { useMemo, useRef } from "react";

import { Heading, Loading } from "@phoenix/components";
import { useTheme } from "@phoenix/contexts";

export interface ValidationIssue {
  field_path: string;
  message: string;
  line?: number | null;
  severity: string;
}

export interface ModelCostItem {
  model_name: string;
  is_judge: boolean;
  estimated_calls: number;
  estimated_prompt_tokens: number;
  estimated_completion_tokens: number;
  estimated_cost_usd: number;
  is_priced: boolean;
}

export interface CostEstimateData {
  total_probes: number;
  raw_probe_count: number;
  is_truncated_by_ceiling: boolean;
  max_requests_ceiling: number;
  estimated_prompt_tokens: number;
  estimated_completion_tokens: number;
  estimated_total_tokens: number;
  estimated_cost_nominal_usd: number;
  estimated_cost_max_usd: number;
  breakdown_target_usd: number;
  breakdown_evaluator_usd: number;
  models: ModelCostItem[];
  configured_budget_usd?: number | null;
  will_exceed_budget: boolean;
  budget_action?: string | null;
  pricing_coverage: "full" | "partial" | "unpriced";
  unpriced_models: string[];
}

export interface YAMLEditorProps {
  value: string;
  onChange: (value: string) => void;
  onSwitchToForm: () => void;
  onRunScan: () => void;
  onRevertToForm?: () => void;
  isScanning?: boolean;
  issues?: ValidationIssue[];
  isValid?: boolean;
  isValidating?: boolean;
  onValidate?: () => void;
  hasAdvancedFields?: boolean;

  // File Management & Revision History
  activeConfigName?: string | null;
  hasUnsavedChanges?: boolean;
  onSave?: () => void;
  onSaveAs?: () => void;
  onOpenLoadModal?: () => void;
  onImportYaml?: (fileContent: string) => void;
  onExportYaml?: () => void;
  onFormatYaml?: () => void;

  // Cost Estimation
  onEstimateCost?: () => void;
  isEstimatingCost?: boolean;
  costEstimate?: CostEstimateData | null;
  costEstimateError?: string | null;

  // Revision History
  currentRevisionNumber?: number | null;
  onOpenRevisionHistory?: () => void;
}

// =========================================================================
// SVG ICONS
// =========================================================================

const SparkleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" />
  </svg>
);

const DownloadTrayIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const UploadTrayIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);

const FolderIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
  </svg>
);

const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const RotateCcwIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const SaveFloppyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

// =========================================================================
// STYLES
// =========================================================================

const containerStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
`;

const cardHeaderStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
  flex-wrap: wrap;
  gap: 12px;
`;

const cardToolbarStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-bottom: 1px solid #e2e8f0;
  background-color: #ffffff;
  flex-wrap: wrap;
`;

const toolbarBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "5px 12px",
  borderRadius: "6px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  color: "#334155",
  fontSize: "12px",
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
  transition: "all 0.15s ease",
  fontFamily: "'Century Gothic', CenturyGothic, AppleGothic, sans-serif",
};

const editorAreaStyle = css`
  flex: 1;
  min-height: 480px;
  overflow: auto;
  font-family: 'JetBrains Mono', 'Fira Code', Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  background-color: #ffffff;

  .cm-editor {
    height: 100%;
    min-height: 480px;
    background-color: #ffffff;
    font-family: 'JetBrains Mono', 'Fira Code', Menlo, Monaco, Consolas, monospace;
  }
  .cm-scroller {
    overflow: auto;
    font-family: inherit;
    line-height: 1.6;
  }
  .cm-gutters {
    background-color: #ffffff;
    color: #94a3b8;
    border-right: 1px solid #f1f5f9;
  }
  .cm-lineNumbers .cm-gutterElement {
    padding: 0 12px 0 16px;
    min-width: 38px;
    text-align: right;
    color: #94a3b8;
    font-size: 12px;
  }
  .cm-activeLine {
    background-color: #f0f9ff !important;
  }
  .cm-activeLineGutter {
    background-color: #f0f9ff !important;
    color: #0f172a !important;
    font-weight: 700;
  }
  .cm-line {
    padding: 0 16px;
  }
  /* Syntax token colors matching theme reference image */
  .tok-keyword, .tok-propertyName, .tok-definition, .cm-keyword, .cm-property, .cm-def {
    color: #b91c1c !important;
    font-weight: 600 !important;
  }
  .tok-string, .cm-string {
    color: #0284c7 !important;
  }
  .tok-number, .cm-number {
    color: #2563eb !important;
    font-weight: 600 !important;
  }
  .tok-comment, .cm-comment {
    color: #94a3b8 !important;
    font-style: italic !important;
  }
  .tok-atom, .cm-atom {
    color: #7c3aed !important;
  }
`;

const bannerStyle = (isError: boolean) => css`
  padding: 8px 16px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${isError ? "#fef2f2" : "#f0fdf4"};
  border-bottom: 1px solid ${isError ? "#fecaca" : "#bbf7d0"};
  color: ${isError ? "#991b1b" : "#166534"};
`;

const costEstimateBannerStyle = (exceedsBudget: boolean) => css`
  padding: 10px 16px;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background-color: ${exceedsBudget ? "#fffbeb" : "#f8fafc"};
  border-bottom: 1px solid ${exceedsBudget ? "#fde68a" : "#e2e8f0"};
  color: #1e293b;
`;

const issuesPanelStyle = css`
  max-height: 160px;
  overflow-y: auto;
  padding: 10px 16px;
  border-top: 1px solid #fecaca;
  background-color: #fff5f5;
  font-size: 12px;
`;

export function YAMLEditor({
  value,
  onChange,
  onSwitchToForm,
  onRunScan,
  onRevertToForm,
  isScanning = false,
  issues = [],
  isValid = true,
  isValidating = false,
  onValidate,
  hasAdvancedFields = false,
  activeConfigName = null,
  hasUnsavedChanges = false,
  onSave,
  onSaveAs,
  onOpenLoadModal,
  onImportYaml,
  onExportYaml,
  onFormatYaml,
  onEstimateCost,
  isEstimatingCost = false,
  costEstimate = null,
  costEstimateError = null,
  currentRevisionNumber = null,
  onOpenRevisionHistory,
}: YAMLEditorProps) {
  const { theme } = useTheme();
  const codeMirrorTheme = theme === "light" ? githubLight : githubDark;
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".yaml") && !file.name.endsWith(".yml")) {
      alert("Please select a valid .yaml or .yml file.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text === "string" && onImportYaml) {
        onImportYaml(text);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  // Configure CodeMirror extensions with YAML language and dynamic linting
  const extensions = useMemo(() => {
    const yamlLanguage = StreamLanguage.define(yaml);

    const yamlLinter = linter((view) => {
      const diagnostics: Diagnostic[] = [];
      const doc = view.state.doc;

      for (const issue of issues) {
        let from = 0;
        let to = 0;

        if (typeof issue.line === "number" && issue.line > 0) {
          const lineNum = Math.min(issue.line, doc.lines);
          const lineObj = doc.line(lineNum);
          from = lineObj.from;
          to = lineObj.to;
        } else {
          from = 0;
          to = Math.min(doc.length, 50);
        }

        diagnostics.push({
          from,
          to,
          severity: issue.severity === "warning" ? "warning" : "error",
          message: `${issue.field_path ? `[${issue.field_path}] ` : ""}${issue.message}`,
        });
      }

      return diagnostics;
    });

    return [
      yamlLanguage,
      EditorView.lineWrapping,
      keymap.of(defaultKeymap),
      yamlLinter,
    ];
  }, [issues]);

  const handleJumpToLine = (line?: number | null) => {
    if (!line || !editorRef.current?.view) return;
    const view = editorRef.current.view;
    const doc = view.state.doc;
    const lineNum = Math.min(Math.max(1, line), doc.lines);
    const lineObj = doc.line(lineNum);
    view.dispatch({
      selection: { anchor: lineObj.from },
      scrollIntoView: true,
    });
    view.focus();
  };

  const lineCount = value ? value.split("\n").length : 0;
  const charCount = value ? value.length : 0;

  return (
    <div css={containerStyle}>
      {/* 1. Card Top Header */}
      <div css={cardHeaderStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'JetBrains Mono', Menlo, monospace",
              fontWeight: 700,
              color: "#334155",
              fontSize: "13px",
            }}
          >
            {"</>"}
          </div>
          <div>
            <div style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>
              YAML Editor
            </div>
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "1px" }}>
              Edit your configuration in YAML format. Changes are automatically validated and synced with the visual form.
            </div>
          </div>
        </div>

        {onFormatYaml && (
          <button
            type="button"
            onClick={onFormatYaml}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 14px",
              borderRadius: "6px",
              border: "1px solid #cbd5e1",
              backgroundColor: "#ffffff",
              color: "#0f766e",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              transition: "all 0.15s ease",
            }}
            title="Format and clean up YAML indentation"
          >
            <SparkleIcon />
            <span>Format YAML</span>
          </button>
        )}
      </div>

      {/* 2. Card Action Toolbar */}
      <div css={cardToolbarStyle}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".yaml,.yml"
          style={{ display: "none" }}
        />

        {onImportYaml && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={toolbarBtnStyle}
            title="Import YAML file"
          >
            <DownloadTrayIcon />
            <span>Import</span>
          </button>
        )}

        {onExportYaml && (
          <button
            type="button"
            onClick={onExportYaml}
            style={toolbarBtnStyle}
            title="Download YAML file"
          >
            <UploadTrayIcon />
            <span>Export</span>
          </button>
        )}

        {onOpenLoadModal && (
          <button
            type="button"
            onClick={onOpenLoadModal}
            style={toolbarBtnStyle}
            title="Load saved configuration"
          >
            <FolderIcon />
            <span>Load...</span>
          </button>
        )}

        {onOpenRevisionHistory && (
          <button
            type="button"
            onClick={onOpenRevisionHistory}
            style={toolbarBtnStyle}
            title={activeConfigName ? "View revision history" : "Save configuration to track history"}
          >
            <ClockIcon />
            <span>{currentRevisionNumber ? `History (v${currentRevisionNumber})` : "History (v1)"}</span>
          </button>
        )}

        <div style={{ width: "1px", height: "18px", backgroundColor: "#cbd5e1", margin: "0 4px" }} />

        {onValidate && (
          <button
            type="button"
            onClick={onValidate}
            disabled={isValidating}
            style={{
              ...toolbarBtnStyle,
              borderColor: isValid ? "#bbf7d0" : "#fecaca",
              color: isValid ? "#15803d" : "#dc2626",
              fontWeight: 600,
            }}
            title="Validate YAML configuration"
          >
            <CheckIcon />
            <span>{isValidating ? "Validating..." : "Validate YAML"}</span>
          </button>
        )}

        {onRevertToForm && (
          <button
            type="button"
            onClick={onRevertToForm}
            style={toolbarBtnStyle}
            title="Discard changes and revert to Visual Form state"
          >
            <RotateCcwIcon />
            <span>Discard &amp; Revert</span>
          </button>
        )}

        {onSaveAs && (
          <button
            type="button"
            onClick={onSaveAs}
            disabled={!isValid || isValidating}
            style={{
              ...toolbarBtnStyle,
              color: isValid ? "#64748b" : "#94a3b8",
            }}
            title="Save as a new configuration"
          >
            <SaveFloppyIcon />
            <span>Save As...</span>
          </button>
        )}
      </div>

      {/* Info / Notice Banner */}
      {hasAdvancedFields && isValid && (
        <div css={bannerStyle(false)}>
          <span>
            ℹ <strong>Advanced YAML Settings Detected:</strong> Budget limits, quality gates, and custom execution settings are preserved and will apply directly during this scan.
          </span>
        </div>
      )}

      {/* Cost Estimate Error Banner */}
      {costEstimateError && (
        <div css={bannerStyle(true)}>
          <span>⚠ <strong>Cost Estimation Error:</strong> {costEstimateError}</span>
        </div>
      )}

      {/* Cost Estimate Preview Banner */}
      {costEstimate && (
        <div css={costEstimateBannerStyle(costEstimate.will_exceed_budget)}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
              <span>
                <strong>Probes:</strong> {costEstimate.total_probes}
              </span>
              <span>
                <strong>Est. Tokens:</strong> ~{costEstimate.estimated_total_tokens.toLocaleString()}
              </span>
              <span>
                <strong>Est. Cost:</strong>{" "}
                <span style={{ fontWeight: 700, color: "#0f766e" }}>
                  ${costEstimate.estimated_cost_nominal_usd.toFixed(4)}
                </span>
              </span>
            </div>

            <div>
              {costEstimate.configured_budget_usd != null && (
                costEstimate.will_exceed_budget ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "2px 8px",
                      borderRadius: "9999px",
                      backgroundColor: "#fee2e2",
                      color: "#b91c1c",
                      fontWeight: 600,
                    }}
                  >
                    ⚠ Exceeds Budget (${costEstimate.configured_budget_usd.toFixed(2)})
                  </span>
                ) : (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "2px 8px",
                      borderRadius: "9999px",
                      backgroundColor: "#dcfce7",
                      color: "#15803d",
                      fontWeight: 600,
                    }}
                  >
                    ✓ Within Budget (${costEstimate.configured_budget_usd.toFixed(2)})
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* CodeMirror Editor Area */}
      <div css={editorAreaStyle}>
        <CodeMirror
          ref={editorRef}
          value={value}
          theme={codeMirrorTheme}
          extensions={extensions}
          onChange={onChange}
          height="100%"
          minHeight="480px"
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLineGutter: true,
            highlightActiveLine: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: false,
          }}
        />
      </div>

      {/* Diagnostics Panel (Rendered when issues exist) */}
      {issues.length > 0 && (
        <div css={issuesPanelStyle}>
          <Heading level={6} style={{ color: "#991b1b", marginBottom: "6px" }}>
            Configuration Issues ({issues.length})
          </Heading>
          <ul style={{ margin: 0, paddingLeft: "18px", color: "#b91c1c" }}>
            {issues.map((issue, idx) => (
              <li
                key={`${issue.field_path}-${issue.line}-${idx}`}
                style={{
                  marginBottom: "4px",
                  cursor: issue.line ? "pointer" : "default",
                }}
                onClick={() => handleJumpToLine(issue.line)}
                title={issue.line ? `Click to jump to line ${issue.line}` : undefined}
              >
                {issue.line ? <strong>Line {issue.line}: </strong> : null}
                {issue.field_path && issue.field_path !== "root" ? (
                  <code
                    style={{
                      backgroundColor: "rgba(0,0,0,0.06)",
                      padding: "1px 4px",
                      borderRadius: "3px",
                      marginRight: "4px",
                    }}
                  >
                    {issue.field_path}
                  </code>
                ) : null}
                <span>{issue.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 3. Card Bottom Status Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 20px",
          borderTop: "1px solid #e2e8f0",
          backgroundColor: "#f8fafc",
          fontSize: "12px",
          color: "#64748b",
          fontFamily: "'JetBrains Mono', Menlo, Consolas, monospace",
          userSelect: "none",
        }}
      >
        <div>YAML schema version: 1.0</div>
        <div>
          Lines: {lineCount}&nbsp;&nbsp;&nbsp;Characters: {charCount}
        </div>
      </div>
    </div>
  );
}
